import { RequestQuoteFormData } from "src/pages/TicketForm/RequestQuoteForm/types";
import { PurchaseOrderRecord } from "src/services/Firebase/data";

const loadHtml2PdfLibrary = async () => {
  if ((window as any).html2pdf) {
    return;
  }

  const script = document.createElement("script");
  script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";

  await new Promise<void>((resolve, reject) => {
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load html2pdf library"));
    document.head.appendChild(script);
  });
};

/**
 * Generates a PDF from RFQ form data and triggers download
 * @param formData - The form data to convert to PDF
 * @param fileName - Name of the PDF file (without extension)
 */
export const generateAndDownloadRFQPdf = async (formData: RequestQuoteFormData, fileName: string = "RFQ") => {
  try {
    await loadHtml2PdfLibrary();

    // Create HTML content for the PDF
    const htmlContent = generateRFQHtmlContent(formData);

    // Create temporary container for PDF generation
    const element = document.createElement("div");
    element.innerHTML = htmlContent;
    element.style.position = "absolute";
    element.style.left = "-9999px";
    document.body.appendChild(element);

    // Configure PDF options
    const opt = {
      margin: 10,
      filename: `${fileName}_${new Date().toISOString().split("T")[0]}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: "portrait", unit: "mm", format: "a4" },
    };

    // Generate PDF (using window.html2pdf if available)
    const html2pdf = (window as any).html2pdf;
    if (!html2pdf) {
      throw new Error("html2pdf library not available");
    }

    html2pdf().set(opt).from(element).save();

    // Cleanup
    document.body.removeChild(element);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};

export const generateAndDownloadPurchaseOrderPdf = async (
  purchaseOrder: PurchaseOrderRecord,
  fileName: string = "Purchase_Order",
) => {
  try {
    await loadHtml2PdfLibrary();

    const htmlContent = generatePurchaseOrderHtmlContent(purchaseOrder);

    const element = document.createElement("div");
    element.innerHTML = htmlContent;
    element.style.position = "absolute";
    element.style.left = "-9999px";
    document.body.appendChild(element);

    const opt = {
      margin: 10,
      filename: `${fileName}_${new Date().toISOString().split("T")[0]}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: "portrait", unit: "mm", format: "a4" },
    };

    const html2pdf = (window as any).html2pdf;
    if (!html2pdf) {
      throw new Error("html2pdf library not available");
    }

    html2pdf().set(opt).from(element).save();

    document.body.removeChild(element);
  } catch (error) {
    console.error("Error generating purchase-order PDF:", error);
    throw error;
  }
};

/**
 * Generates HTML content for the RFQ PDF
 */
const generateRFQHtmlContent = (formData: RequestQuoteFormData): string => {
  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
  const formatDate = () => new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return `
    <html>
      <head>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .header h1 {
            margin: 0;
            font-size: 28px;
            color: #1a1a1a;
          }
          .header p {
            margin: 5px 0;
            font-size: 12px;
            color: #666;
          }
          .section {
            margin-bottom: 25px;
          }
          .section-title {
            font-size: 14px;
            font-weight: bold;
            background-color: #f0f0f0;
            padding: 8px 10px;
            margin-bottom: 10px;
            border-left: 4px solid #333;
          }
          .address-section {
            display: flex;
            gap: 40px;
            margin-bottom: 25px;
          }
          .address-block {
            flex: 1;
          }
          .address-block .label {
            font-weight: bold;
            margin-bottom: 5px;
            font-size: 12px;
          }
          .address-block .content {
            font-size: 12px;
            line-height: 1.5;
            color: #555;
            white-space: pre-wrap;
          }
          .description {
            font-size: 12px;
            line-height: 1.6;
            color: #555;
            white-space: pre-wrap;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 12px;
          }
          th {
            background-color: #f0f0f0;
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            font-weight: bold;
          }
          td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          td.numeric {
            text-align: right;
          }
          .total-row {
            background-color: #f9f9f9;
            font-weight: bold;
          }
          .grand-total {
            background-color: #e8e8e8;
            font-weight: bold;
            font-size: 14px;
          }
          .empty-cell {
            color: #999;
            font-style: italic;
          }
          .date {
            font-size: 12px;
            color: #666;
            text-align: right;
            margin-top: 20px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>REQUEST FOR QUOTATION (RFQ)</h1>
          <p>Date: ${formatDate()}</p>
        </div>

        <div class="address-section">
          <div class="address-block">
            <div class="label">BILL TO:</div>
            <div class="content">
Attention To: ${formData.billTo.attentionTo || "(Not specified)"}
${formData.billTo.client || "(Not specified)"}
${formData.billTo.address || "(No address provided)"}
            </div>
          </div>
          <div class="address-block">
            <div class="label">SHIP TO:</div>
            <div class="content">
${
  formData.shipTo.sameAsAddress
    ? "Same as Bill To address"
    : `Attention To: ${formData.shipTo.attentionTo || "(Not specified)"}\n${
        formData.shipTo.client || "(Not specified)"
      }\n${formData.shipTo.address || "(No address provided)"}`
}
            </div>
          </div>
        </div>

        ${
          formData.quotationDescription.description || formData.quotationDescription.vendorName
            ? `
        <div class="section">
          <div class="section-title">QUOTATION DESCRIPTION</div>
          ${
            formData.quotationDescription.description
              ? `<div class="description"><strong>Description:</strong>\n${formData.quotationDescription.description}</div>`
              : ""
          }
          ${
            formData.quotationDescription.vendorName
              ? `<div class="description" style="margin-top: 10px;"><strong>Vendor Name:</strong> ${formData.quotationDescription.vendorName}</div>`
              : ""
          }
        </div>
        `
            : ""
        }

        ${
          formData.laborDetails && formData.laborDetails.length > 0
            ? `
        <div class="section">
          <div class="section-title">LABOR DETAILS</div>
          <table>
            <thead>
              <tr>
                <th>Service per Month</th>
                <th>Rate</th>
                <th style="text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${formData.laborDetails
                .map(
                  (row) => `
              <tr>
                <td class="numeric">${row.servicePerMonth}</td>
                <td class="numeric">${formatCurrency(row.rate)}</td>
                <td class="numeric">${formatCurrency(row.total)}</td>
              </tr>
              `,
                )
                .join("")}
              <tr class="total-row">
                <td colspan="2" style="text-align: right;">Labour Total:</td>
                <td class="numeric">${formatCurrency(formData.laborTotalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        `
            : ""
        }

        ${
          formData.serviceDetails && formData.serviceDetails.length > 0
            ? `
        <div class="section">
          <div class="section-title">SERVICE DETAILS</div>
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Rate</th>
                <th>Quantity</th>
                <th style="text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${formData.serviceDetails
                .map(
                  (row) => `
              <tr>
                <td>${row.service || "<span class='empty-cell'>-</span>"}</td>
                <td class="numeric">${formatCurrency(row.rate)}</td>
                <td class="numeric">${row.quantity}</td>
                <td class="numeric">${formatCurrency(row.total)}</td>
              </tr>
              `,
                )
                .join("")}
              <tr class="total-row">
                <td colspan="3" style="text-align: right;">Service Total:</td>
                <td class="numeric">${formatCurrency(formData.serviceTotalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        `
            : ""
        }

        ${
          formData.equipmentDetails && formData.equipmentDetails.length > 0
            ? `
        <div class="section">
          <div class="section-title">EQUIPMENT DETAILS</div>
          <table>
            <thead>
              <tr>
                <th>Equipment</th>
                <th>Rate</th>
                <th>Quantity</th>
                <th style="text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${formData.equipmentDetails
                .map(
                  (row) => `
              <tr>
                <td>${row.equipment || "<span class='empty-cell'>-</span>"}</td>
                <td class="numeric">${formatCurrency(row.rate)}</td>
                <td class="numeric">${row.quantity}</td>
                <td class="numeric">${formatCurrency(row.total)}</td>
              </tr>
              `,
                )
                .join("")}
              <tr class="total-row">
                <td colspan="3" style="text-align: right;">Equipment Total:</td>
                <td class="numeric">${formatCurrency(formData.equipmentTotalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        `
            : ""
        }

        ${
          formData.materialDetails && formData.materialDetails.length > 0
            ? `
        <div class="section">
          <div class="section-title">MATERIAL DETAILS</div>
          <table>
            <thead>
              <tr>
                <th>Material</th>
                <th>Rate</th>
                <th>Quantity</th>
                <th style="text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${formData.materialDetails
                .map(
                  (row: { material: string; rate: number; quantity: number; total: number }) => `
              <tr>
                <td>${row.material || "<span class='empty-cell'>-</span>"}</td>
                <td class="numeric">${formatCurrency(row.rate)}</td>
                <td class="numeric">${row.quantity}</td>
                <td class="numeric">${formatCurrency(row.total)}</td>
              </tr>
              `,
                )
                .join("")}
              <tr class="total-row">
                <td colspan="3" style="text-align: right;">Material Total:</td>
                <td class="numeric">${formatCurrency(formData.materialTotalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        `
            : ""
        }

        ${
          formData.additionalFees && formData.additionalFees.length > 0
            ? `
        <div class="section">
          <div class="section-title">ADDITIONAL FEES</div>
          <table>
            <thead>
              <tr>
                <th>Fee Type</th>
                <th style="text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${formData.additionalFees
                .map(
                  (row: { typeOfFee: string; total: number }) => `
              <tr>
                <td>${row.typeOfFee || "<span class='empty-cell'>-</span>"}</td>
                <td class="numeric">${formatCurrency(row.total)}</td>
              </tr>
              `,
                )
                .join("")}
              <tr class="total-row">
                <td style="text-align: right;">Additional Fees Total:</td>
                <td class="numeric">${formatCurrency(formData.additionalFeesTotalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        `
            : ""
        }

        <div class="section">
          <table>
            <tr class="grand-total">
              <td style="text-align: right; width: 75%;">QUOTE TOTAL AMOUNT:</td>
              <td class="numeric" style="width: 25%;">${formatCurrency(formData.quoteTotalAmount)}</td>
            </tr>
          </table>
        </div>

        ${
          formData.scopeOfWork.scopeOfWork || formData.scopeOfWork.markupPercentage > 0
            ? `
        <div class="section">
          <div class="section-title">SCOPE OF WORK</div>
          ${
            formData.scopeOfWork.scopeOfWork
              ? `<div class="description"><strong>Details:</strong>\n${formData.scopeOfWork.scopeOfWork}</div>`
              : ""
          }
          ${
            formData.scopeOfWork.markupPercentage > 0
              ? `<div class="description" style="margin-top: 10px;"><strong>Markup:</strong> ${formData.scopeOfWork.markupPercentage}%</div>`
              : ""
          }
        </div>
        `
            : ""
        }

        <div class="date">
          <strong>Quote Next Step:</strong> ${
            formData.quoteNextStep === "dispatch"
              ? "Dispatch to send quote to client"
              : formData.quoteNextStep === "manager"
                ? "Manager to send quote to client"
                : "Not specified"
          }
        </div>
      </body>
    </html>
  `;
};

const generatePurchaseOrderHtmlContent = (purchaseOrder: PurchaseOrderRecord): string => {
  const getDocumentTypeLabel = () => {
    if (purchaseOrder.documentType === "blanket_purchase_order") return "Blanket Purchase Order";
    if (purchaseOrder.documentType === "purchase_requisition") return "Purchase Requisition";
    return "Purchase Order";
  };

  const formatDate = (value?: string) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (value: number) => `$${Number(value || 0).toFixed(2)}`;

  return `
    <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #222; }
          .header { border-bottom: 2px solid #222; padding-bottom: 14px; margin-bottom: 18px; }
          .header h1 { margin: 0; font-size: 24px; }
          .header .meta { margin-top: 6px; font-size: 12px; color: #555; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 16px; }
          .card { border: 1px solid #ddd; padding: 10px; }
          .card .title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: #666; margin-bottom: 6px; font-weight: bold; }
          .card .value { font-size: 12px; line-height: 1.5; white-space: pre-wrap; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 7px; text-align: left; }
          th { background: #f1f1f1; }
          td.num, th.num { text-align: right; }
          .totals { margin-top: 12px; width: 100%; max-width: 360px; margin-left: auto; }
          .totals-row { display: flex; justify-content: space-between; border-bottom: 1px solid #ddd; padding: 6px 0; font-size: 12px; }
          .totals-row.total { font-weight: bold; font-size: 14px; border-top: 2px solid #222; border-bottom: 0; margin-top: 4px; padding-top: 8px; }
          .footer { margin-top: 20px; font-size: 12px; }
          .label { color: #666; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${getDocumentTypeLabel()}</h1>
          <div class="meta">
            Number: ${purchaseOrder.poNumber || "-"} | Date: ${formatDate(purchaseOrder.poDate)} | Status: ${purchaseOrder.status || "draft"}
          </div>
        </div>

        <div style="margin-bottom: 12px;"><span class="label">Description:</span> ${purchaseOrder.description || "-"}</div>

        <div class="grid">
          <div class="card">
            <div class="title">Company</div>
            <div class="value">${purchaseOrder.companyName || "-"}</div>
            <div class="title" style="margin-top: 10px;">Billing Address</div>
            <div class="value">${purchaseOrder.billingAddress || "-"}</div>
            <div class="title" style="margin-top: 10px;">Shipping Address</div>
            <div class="value">${purchaseOrder.shippingAddress || "-"}</div>
          </div>
          <div class="card">
            <div class="title">Contact</div>
            <div class="value">${purchaseOrder.contact?.name || "-"}\n${purchaseOrder.contact?.phone || "-"}\n${purchaseOrder.contact?.email || "-"}</div>
            <div class="title" style="margin-top: 10px;">Vendor</div>
            <div class="value">${purchaseOrder.vendorName || "-"}\n${purchaseOrder.vendorAddress || "-"}\n${purchaseOrder.vendorContactInfo || "-"}\nVendor ID: ${purchaseOrder.vendorId || "-"}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Service Type</th>
              <th>SKU</th>
              <th class="num">Qty</th>
              <th>UOM</th>
              <th class="num">Unit Price</th>
              <th class="num">Line Total</th>
            </tr>
          </thead>
          <tbody>
            ${(purchaseOrder.lineItems || [])
              .map(
                (item) => `
              <tr>
                <td>${item.description || "-"}</td>
                <td>${item.serviceType || "-"}</td>
                <td>${item.sku || "-"}</td>
                <td class="num">${Number(item.quantity || 0)}</td>
                <td>${item.unitOfMeasure || "-"}</td>
                <td class="num">${formatCurrency(item.unitPrice || 0)}</td>
                <td class="num">${formatCurrency(item.lineTotal || 0)}</td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>

        <div class="totals">
          <div class="totals-row"><span>Subtotal</span><span>${formatCurrency(purchaseOrder.subtotal || 0)}</span></div>
          <div class="totals-row"><span>Tax</span><span>${formatCurrency(purchaseOrder.tax || 0)}</span></div>
          <div class="totals-row"><span>Shipping/Freight</span><span>${formatCurrency(purchaseOrder.shippingCost || 0)}</span></div>
          <div class="totals-row total"><span>Total</span><span>${formatCurrency(purchaseOrder.totalAmount || 0)}</span></div>
        </div>

        <div class="footer">
          <div><span class="label">Requested Delivery Date:</span> ${formatDate(purchaseOrder.requestedDeliveryDate)}</div>
          <div><span class="label">Approver:</span> ${purchaseOrder.approverName || "-"}</div>
          <div><span class="label">Linked Work Order:</span> ${purchaseOrder.linkedWonum || "-"}</div>
          <div style="margin-top: 8px;"><span class="label">Notes:</span> ${purchaseOrder.notes || "-"}</div>
        </div>
      </body>
    </html>
  `;
};
